import {IParser, Impact, IIncident, IIncidentRaw, Incident, IMaintanance, Maintanance, IMaintananceRaw, IExternalIncident, ExternalIncident, IExternalIncidentRaw, IGlobalMessageRaw, GlobalNews, IGlobalMessage, ICategory, Category, ICategoryRaw, IService, Service, IServiceRaw, ServiceState, ServiceStateNumbers, IServiceTree} from './rz-status_models'
import Constants from './rz-status_constants'

class Parser implements IParser {
    incidents: IIncident[];
    maintenances: IMaintanance[];
    maintenanceIDs: number[];
    incidentIDs: number[];

    /**
    * New version of momentJS will not create Date with Year 0 in case of null dates, but instead
    * create an "Invalid date" instance of the date object.
    * To make sure, we get something our renderer can parse, we only return a date if it is valid - 
    * otherwise, we will return "null", indicating that there is no (parsable) date
    **/
    private safeToDateConverter (datestr) {
        if ((typeof(datestr) === "undefined") || (datestr == null)) { return null;}
        var m = moment(datestr, Constants.DateFormat.jsonFormat);
        return ((m.isValid) ? m.toDate() : null);
    }

    getImpactFromString(input: string) :Impact {
        
        if (input == Constants.Impact.outage) {
            return Impact.Outage;
        }
        if (input == Constants.Impact.limited) {
            return Impact.Limited;
        }

        return Impact.Ready;
    }

    parseIncidents(incidentsJson: string) : Array<IIncident> {
        var result = new Array<IIncident>();
        var incidents: Array<IIncidentRaw> = JSON.parse(incidentsJson);

        if (incidents != null) {

            for (var i = 0; i < incidents.length; i++) {
                var currentRaw = incidents[i];
                var item: IIncident = new Incident();
                
                item.worstState = this.getImpactFromString(currentRaw.worstState);
                item.currentState = this.getImpactFromString(currentRaw.currentState);
                item.endDate = this.safeToDateConverter(currentRaw.endDate);
                item.startDate = this.safeToDateConverter(currentRaw.startDate);
                item.serviceId = currentRaw.serviceInfoDto;
                item.externalInfo = currentRaw.externalInfo;

                result[result.length] = item;
            }
        }

        return result; 
    }


    parseMaintanances(maintanancesJson: string, current: boolean = false) :Array<IMaintanance> {
        
        var result = new Array <IMaintanance>();
        var maintanances: Array<IMaintananceRaw> = JSON.parse(maintanancesJson);

        if (maintanances != null) {

            for (var i = 0; i < maintanances.length; i++) {
                var currentRaw = maintanances[i];
                var item: IMaintanance = new Maintanance();
                
                item.ticketId = currentRaw.ticketId;
                item.descriptionLong = currentRaw.descriptionLong.trim();
                item.affectedServicesInfo = currentRaw.affectedServicesInfo;
                item.endDate = this.safeToDateConverter(currentRaw.endDate);
                item.startDate = this.safeToDateConverter(currentRaw.startDate);
                item.lastEdit = this.safeToDateConverter(currentRaw.lastEdit);
                

                result[result.length] = item;
            }
        }

        if(current) {
            var now = new Date();
            return result.filter(m => (m.startDate < now && m.endDate > now))
        }

        return result;        
    }

    /**
    * Parse a JSON string of external incidents and return the external incidents as data objects
    * Format of the JSON string: [{"Id":68,"Header":"Beispiel Störung xy","Text":"..."},...]
    * @param externalJson a JSON string 
    * @preturn an array of external incidents
    **/
    parseExternalIncidents (externalJson:string) : Array<IExternalIncident> {
        var result = new Array<IExternalIncident>();
        var incidents: Array<IExternalIncidentRaw> = JSON.parse(externalJson);

        if (incidents != null) {
            for (var i = 0; i < incidents.length; i++) {
                var currentRaw:IExternalIncidentRaw =  incidents[i];
                var item: IExternalIncident = new ExternalIncident();
                item.id = currentRaw.id;
                item.title = currentRaw.title;
                item.markdown = currentRaw.markdown;
                result[result.length] = item;
            }
        }
        return(result);
    }
    /**
    * Parse a JSON string of notifications and return the notifications as data objects
    * Format of the JSON string: [{"Text":"Es liegt akutell eine umfangreiche Störung im Rechenzentrum vor. \r\n\r\n\r\n","Extern":true}]
    * @param externalJson a JSON string 
    * @preturn an array of notification objects
    **/
    parseNotifications (notificationJson:string) : Array<IGlobalMessage> {
        var result = new Array<IGlobalMessage>();
        var notifications: Array<IGlobalMessageRaw> = JSON.parse(notificationJson);

        if (notifications != null) {
            for (var i = 0; i < notifications.length; i++) {
                var currentRaw:IGlobalMessageRaw =  notifications[i];
                var item: IGlobalMessage = new GlobalNews();
                item.text = currentRaw.text;
                item.isExternal = currentRaw.isExternal;
                result[result.length] = item;
            }
        }
        return(result);
    }

    parseCategories(categoriesJson: string, incidents: IIncident[] | null = null, maintenances: IMaintanance[] | null = null) : IServiceTree {
        if(incidents != null)
            this.incidents = incidents;
        if(maintenances != null)
            this.maintenances = maintenances
        this.maintenanceIDs = Array.from(new Set(maintenances?.flatMap(item => item.affectedServicesInfo.map(s => s.id))));
        this.incidentIDs = Array.from(new Set(incidents?.flatMap(item => item.serviceId.id)));
        var result = new Array<ICategory>();
        var categories: Array<ICategoryRaw> = JSON.parse(categoriesJson);

        if (categories != null && Array.isArray(categories)) {
            categories.forEach(cateogryRaw => {
                result.push(this.parseCategoryRecursive(cateogryRaw))
            });
        }
        result.sort(this.sortCategories)
        const states = this.mergeCounts(...result.map(c => c.states))
        return {categories: result, states, incidents: incidents}; 
    }

    parseCategoryRecursive(categoryRaw: ICategoryRaw): ICategory {
        let category = new Category();
        category.id = categoryRaw.id;
        category.name = categoryRaw.name;
        category.services = categoryRaw.services.map(s => this.parseService(s))
        category.services.sort(this.sortByServiceState)
        category.categories = categoryRaw.categories.map(c => this.parseCategoryRecursive(c))
        category.categories.sort(this.sortCategories)
        category.states = this.getStateCount(category)
        return category;
    }

    getStateCount(category: ICategory) : Record<ServiceState, number> {
        let serviceCount = category.services.reduce((acc, service) => {
            acc[service.currentStatus] = (acc[service.currentStatus] || 0) + 1;
            return acc;
        }, {} as Record<ServiceState, number>);

        return this.mergeCounts(serviceCount, ...category.categories.map(c => c.states))
    }

    mergeCounts(...counts: Partial<Record<ServiceState, number>>[]): Record<ServiceState, number> {
        const result: Record<ServiceState, number> = {} as Record<ServiceState, number>;
        
        ServiceStateNumbers.forEach(state => {
            result[state] = counts.reduce((sum, count) => sum + (count[state] ?? 0), 0);
    
        });

    return result;
    }

    sortCategories = (a: ICategory, b: ICategory) => {
        for (const state of ServiceStateNumbers.slice(1).reverse()) {
            if(a.states[state] > 0) {
                if(b.states[state] > 0) 
                    return a.name.localeCompare(b.name);
                return -1
            }
            if(b.states[state] > 0) 
                return 1
        }
        return a.name.localeCompare(b.name);
    }

    sortByServiceState(a: IService, b:IService) {
        if (a.currentStatus == b.currentStatus)
            return a.name.localeCompare(b.name);
        return b.currentStatus - a.currentStatus;
    }
    
    parseService(serviceRaw: IServiceRaw) : IService {
        let service = new Service();
        service.id = serviceRaw.id;
        service.name = serviceRaw.name;

        if(this.incidentIDs.includes(service.id) && this.incidents != null) {
            service.incident = this.incidents.find(i => i.serviceId.id == service.id)
            service.currentStatus = service.incident.currentState == Impact.Limited ? ServiceState.Limited : ServiceState.Outage ;
        }
        else if(this.maintenanceIDs.includes(service.id) && this.maintenances != null) {
            service.maintenance = this.maintenances.find(m => m.affectedServicesInfo.find(s => s.id == service.id) != null);
            service.currentStatus = ServiceState.Maintanance;
        }
        else {
            service.currentStatus = ServiceState.Available
        }

        return service;
    }

        getServiceStateFromString(input: string) :ServiceState {
        
        if (input == Constants.Impact.outage) {
            return ServiceState.Outage;
        }
        if (input == Constants.Impact.limited) {
            return ServiceState.Limited;
        }

        return ServiceState.Available;
    }

    async fetchText(url: string): Promise<string | null> {
        try {
        const res = await fetch(url);
        return res.ok ? await res.text() : null

        } catch (err) {
        console.warn(`${url} failed`, err);
        return null;
        }
    };
}

export default Parser;